import React, { useState } from "react";
import TypewriterRendererResponsePlain from "../TypeWriterResponsePlain";
import SpeakerButton from "./SpeakerButton";
export default function LectureSlides(material) {
    // Material contains { title, outline }
    const title = 'AIAT virtual classroom';
    const outline = material;

    // Lecture data returned by backend
    const [lecture, setLecture] = useState(null);
    // Which slide is currently shown
    const [currentSlide, setCurrentSlide] = useState(0);
    // Whether the popup (modal) is open
    const [showModal, setShowModal] = useState(false);
    // Loading states for generating slides & asking questions
    const [loadingLecture, setLoadingLecture] = useState(false);
    const [loadingAnswer, setLoadingAnswer] = useState(false);

    // Q&A
    const [question, setQuestion] = useState("");
    const [answer, setAnswer] = useState("");

    // Create the lecture from material (title & outline)
    const handleGenerateLecture = async () => {
        if (!title || !outline) {
            alert("No valid material supplied.");
            return;
        }

        try {
            setLoadingLecture(true);
            setAnswer("");
            setQuestion("");

            const response = await fetch("/api/virtual-lectures", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ title, outline }),
            });

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || "Failed to create lecture");
            }

            const newLecture = await response.json();
            setLecture(newLecture);
            setCurrentSlide(0);
            setShowModal(true);
        } catch (error) {
            console.error("Error creating lecture:", error);
            alert(error.message || "An error occurred while creating the lecture.");
        } finally {
            setLoadingLecture(false);
        }
    };

    // Ask a question about the current slide
    const handleAskQuestion = async () => {
        if (!lecture || typeof currentSlide !== "number") return;

        try {
            setLoadingAnswer(true);
            setAnswer("");

            const response = await fetch(`/api/virtual-lectures/question`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ currentSlide: lecture.slides[currentSlide], question }),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || "Failed to get answer");
            }

            setAnswer(data.answer);
        } catch (error) {
            console.error("Error answering question:", error);
            alert(error.message || "An error occurred while answering.");
        } finally {
            setLoadingAnswer(false);
        }
    };

    // Slide navigation
    const handleNextSlide = () => {
        if (lecture && currentSlide < lecture.slides.length - 1) {
            setCurrentSlide((prev) => prev + 1);
            setQuestion("");
            setAnswer("");
        }
    };

    const handlePrevSlide = () => {
        if (lecture && currentSlide > 0) {
            setCurrentSlide((prev) => prev - 1);
            setQuestion("");
            setAnswer("");
        }
    };

    return (
        <div className="p-6">
            <div className="flex flex-col items-center justify-center space-y-6">
                {/* Title with Education Icon 🎓 */}
                <h1 className="text-3xl font-extrabold text-gray-800 flex items-center space-x-3">
                    <svg
                        className="w-8 h-8 text-blue-600"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path d="M12 2L1 7l11 5 9-4.09V14h2V7l-11-5zm9 14c-1.1 0-2 .9-2 2h-2a4 4 0 008 0h-2c0-1.1-.9-2-2-2zm-6 0c-1.1 0-2 .9-2 2H9a4 4 0 008 0h-2c0-1.1-.9-2-2-2z"></path>
                    </svg>
                    <span>Enter Virtual Classroom</span>
                </h1>

                {/* Centered Button to Generate Lecture */}
                <button
                    onClick={handleGenerateLecture}
                    className="w-72 h-14 bg-gradient-to-r from-blue-600 to-blue-800 text-white font-semibold rounded-full shadow-lg hover:from-blue-700 hover:to-blue-900 transition-all duration-300 flex items-center justify-center"
                    disabled={loadingLecture}
                >
                    {loadingLecture ? (
                        <>
                            <svg
                                className="animate-spin h-6 w-6 mr-2 text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0116 0h-4l4 4m-4-4h4"></path>
                            </svg>
                            Creating Your Virtual Classroom...
                        </>
                    ) : (
                        "Create Virtual Classroom"
                    )}
                </button>

                {/* Loading Modal */}
                {loadingLecture && (
                    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-md">
                        <div className="bg-white p-8 rounded-2xl shadow-xl flex flex-col items-center animate-fade-in">
                            {/* Bigger Animated Spinner */}
                            <div className="w-16 h-16 border-4 border-blue-500 border-dashed rounded-full animate-spin"></div>

                            {/* Loading Text */}
                            <p className="mt-6 text-xl font-semibold text-gray-700">
                                Setting up your Virtual Classroom...
                            </p>

                            {/* Extra Subtext */}
                            <p className="mt-2 text-sm text-gray-500">
                                Please wait while we prepare everything for you.
                            </p>
                        </div>
                    </div>
                )}
            </div>


            {/* The Popup / Modal */}
            {showModal && lecture && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
                    onClick={() => setShowModal(false)}
                >
                    <div
                        className="bg-white overflow-y-auto rounded-lg shadow-lg max-w-3xl max-h-[90vh] w-full p-6 relative"
                        onClick={(e) => e.stopPropagation()} // Prevent closing on modal content click
                    >
                        {/* Header and Close Button */}
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold">
                                {lecture.title} – Slide {currentSlide + 1} of {lecture.slides.length}
                            </h2>
                            <button
                                onClick={() => setShowModal(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        {/* Slide Content */}
                        <div className="border min-h-[50vh] rounded p-4 mb-4">
                            <h3 className="text-lg font-semibold mb-2">
                                {lecture.slides[currentSlide].title}
                            </h3>
                            <div>

                                <TypewriterRendererResponsePlain content={lecture.slides[currentSlide].content} useTypewriter={true} />

                            </div>
                        </div>

                        {/* Navigation */}
                        <div className="flex space-x-2 mb-4">
                            <button
                                onClick={handlePrevSlide}
                                disabled={currentSlide === 0}
                                className="bg-gray-200 px-3 py-2 rounded hover:bg-gray-300 disabled:opacity-50"
                            >
                                Previous
                            </button>
                            <button
                                onClick={handleNextSlide}
                                disabled={currentSlide === lecture.slides.length - 1}
                                className="bg-gray-200 px-3 py-2 rounded hover:bg-gray-300 disabled:opacity-50"
                            >
                                Next
                            </button>
                            <div className="flex items-center space-x-3 p-4 border-b">
                                <p className="text-lg"></p>
                                <SpeakerButton text={lecture.slides[currentSlide].content} />
                            </div>
                            <></>
                        </div>

                        {/* Question Input */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Ask a question about this slide:
                            </label>
                            <input
                                type="text"
                                value={question}
                                onChange={(e) => setQuestion(e.target.value)}
                                className="border rounded w-full px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
                                placeholder="Type your question here..."
                            />
                            <button
                                onClick={handleAskQuestion}
                                disabled={!question.trim() || loadingAnswer}
                                className="mt-2 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition disabled:opacity-50"
                            >
                                {loadingAnswer ? "Thinking..." : "Ask"}
                            </button>
                        </div>

                        {/* Display the Answer */}
                        {answer && (
                            <div className="bg-gray-100 p-3 rounded">
                                <h4 className="font-semibold mb-2">Answer:</h4>
                                <div>

                                    <TypewriterRendererResponsePlain content={answer} useTypewriter={true}/>

                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
